import React from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
    Spacer,
    Tabs,
    Text,
} from '@zillow/constellation';

import {
    Container,
} from '@zillow/zrs-comet';

// Components
import HeadWrapper from 'app/shared/components/HeadWrapper';

import { RMWContext } from 'app/shared/providers/RMWContext';

// Containers
import AdSpendPage from 'app/shared/containers/AdSpendPage';
import FunnelPage from 'app/shared/containers/FunnelPage';
import OverviewPage from 'app/shared/containers/EnterpriseAnalytics/OverviewPage';
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import PermissionCheck from 'app/shared/components/PermissionCheck';
import MarketPage from 'app/shared/containers/EnterpriseAnalytics/MarketPage';

// Constants
import { ANALYTICS_PAGE } from 'app/shared/constants/RouteConstants';

// Selectors
import { RoutingSelectors, UserSelectors } from 'app/shared/selectors';

import { StyledContainer } from './StyledComponents';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Analytics';

const propTypes = {
    router: PropTypes.object.isRequired,
};

class AnalyticsPage extends React.Component {
    static contextType = RMWContext;

    static contextTypes = {
        brand: PropTypes.string,
        paths: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.state = {
            pageTitle: DEFAULT_PAGE_TITLE,
        };

        this.loadTab = this.loadTab.bind(this);
        this.getActiveTab = this.getActiveTab.bind(this);
        this.getSectionUrl = this.getSectionUrl.bind(this);
        this.renderChildRoutes = this.renderChildRoutes.bind(this);
        this.renderContent = this.renderContent.bind(this);
    }

    getInitialState() {
        return {
            pageTitle: DEFAULT_PAGE_TITLE,
        };
    }

    loadTab(tab) {
        const { paths } = this.context;
        const url = `${paths.analyticsPage}/${tab}`;

        return window.router.transitionTo(url);
    }

    getActiveTab() {
        const { router, cookieEnabledServices } = this.props;
        const { paths } = this.context;
        const marketEnabled = Boolean(cookieEnabledServices.ZG_ANALYTICS_RENT_COMPS_PAGE);

        // Expecting path to be [baseUrl]/rental-manager/analytics/*something*
        const tabName = router.location.pathname.split('/')[3];
        switch (tabName) {
            case paths.analyticsOverview:
                return paths.analyticsOverview;
            case paths.analyticsFunnel:
                return paths.analyticsFunnel;
            case paths.analyticsAdSpend:
                return paths.analyticsAdSpend;
            case paths.analyticsRentComps:
                if (marketEnabled) {
                    return paths.analyticsRentComps;
                }
                return paths.analyticsOverview;
            default:
                return paths.analyticsOverview;
        }
    }

    getSectionUrl(section) {
        const { paths } = this.context;
        return `${paths.analyticsPage}/${section}([?].*)?`;
    }

    /**
     * Child Routes
     *
     * parent path: /rental-manager/analytics
     * child paths: /{section}
     *
     * ex: /rental-manager/analytics/overview
     */
    renderChildRoutes() {
        const { paths } = this.context;
        const { cookieEnabledServices } = this.props;
        const childPathOverview = `${this.getSectionUrl(paths.analyticsOverview)}`;
        const childPathProperties = `${this.getSectionUrl(paths.analyticsFunnel)}`;
        const childPathAdSpend = `${this.getSectionUrl(paths.analyticsAdSpend)}`;
        const childPathRentComps = `${this.getSectionUrl(paths.analyticsRentComps)}`;
        const marketEnabled = Boolean(cookieEnabledServices.ZG_ANALYTICS_RENT_COMPS_PAGE);

        return (
            <Switch>
                {/* /overview */}
                <Route render={() => <OverviewPage />} path={childPathOverview} />
                {/* /properties */}
                <Route render={() => <FunnelPage />} path={childPathProperties} />
                {/* /adspend */}
                <Route render={props => <AdSpendPage location={props.location} />} path={childPathAdSpend} />
                {/* /rentcomps */}
                {marketEnabled && (
                    <Route render={props => <MarketPage location={props.location} />} path={childPathRentComps} />
                )}
            </Switch>
        );
    }

    renderContent() {
        const { paths } = this.context;
        const { cookieEnabledServices } = this.props;
        const marketEnabled = Boolean(cookieEnabledServices.ZG_ANALYTICS_RENT_COMPS_PAGE);
        const tabs = [
            <Tabs.Tab id={paths.analyticsOverview}>Overview</Tabs.Tab>,
            <Tabs.Tab id={paths.analyticsFunnel}>Funnel</Tabs.Tab>,
            <Tabs.Tab id={paths.analyticsAdSpend}>Ad Spend</Tabs.Tab>,
        ];
        if (marketEnabled) {
            tabs.splice(1, 0, <Tabs.Tab id={paths.analyticsRentComps}>Rent Comps</Tabs.Tab>);
        }
        return (
            <Container className="AnalyticsPage-container">
                <StyledContainer borderFull={true} borderRounded={true} className="AnalyticsPage-card">
                    <Spacer paddingY="sm">
                        <Text fontType="h4">
                            {this.state.pageTitle}
                        </Text>
                    </Spacer>
                    <Tabs>
                        <Tabs.List defaultSelected={this.getActiveTab()} onSelected={this.loadTab}>
                            {tabs}
                        </Tabs.List>
                    </Tabs>
                    {this.renderChildRoutes()}
                </StyledContainer>
            </Container>
        );
    }

    render() {
        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <PermissionCheck route={ANALYTICS_PAGE}>
                    <div className="AnalyticsPage">
                        <HeadWrapper title={this.state.pageTitle} />
                        {this.renderContent()}
                    </div>
                </PermissionCheck>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
    router: RoutingSelectors.makeRouting(state),
    cookieEnabledServices: state.app.cookieEnabledServices,
});

AnalyticsPage.propTypes = propTypes;

export { AnalyticsPage };
export default connect(mapStateToProps)(AnalyticsPage);
